import { FC, useEffect, useMemo, useState } from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { Labeled, RadioButtonGroupInput, regex, useRecordContext } from 'react-admin';
import { useQuery } from 'react-query';

import { ScheduleEventCard } from '../../common/components/ScheduleEventCard';
import { ShsSelectInput, ShsTextInput, ShsToggle } from 'src/components/common';
import {
    ConsultationType,
    EducationLevel,
    EducationLevelGroup,
    HIGH_SCHOOL_GRADES_LABELS,
    INVALID_URL_MESSAGE,
    JUNIOR_SCHOOL_GRADES_LABELS,
    PRE_SCHOOL_YEARS_LABELS,
    SUBJECT_TYPE,
    URL_REGEX,
    UserRoleLabel
} from 'src/constants';
import { UserRole } from 'src/auth/types';
import { Theme, palette } from 'src/Theme';
import { ScheduleCalendarEvent } from '../ScheduleCalendar/ScheduleCalendar.types';
import axiosApiInstance from 'src/api/axiosConfig';
import { shsFormErrorStyles } from 'src/styles';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useScheduleCalendarContext } from '../../common/context/CalendarContext';
import { ScheduleEmployee } from '../../common/interfaces/ScheduleEmployee.interface';
import { ScheduleChild } from '../../common/interfaces/ScheduleChild.interface';
import { EducationLevelGradeRange } from 'src/constants/subjects';
import { ScheduleSubject } from '../../common/interfaces/ScheduleSubject.interface';
import DateTimeUtils from 'src/utils/datetime';
import ShsCheckboxTree from 'src/components/common/Fields/ShsCheckboxTree';
import { groupBy } from 'lodash';
import {
    EducationLevelToRolesMap,
    roleSubjectTypesMap,
    UserRoleToAPIPathMap
} from '../../common/const/ScheduleCalendar.const';
import { fetchCenterData, fetchEmployeesData } from '../../common/api';

const StyledBox = styled(Box, {
    shouldForwardProp: (propName) => propName !== 'highlighted'
})<{ highlighted?: boolean }>(({ highlighted = false }) => {
    return {
        width: '100%',
        minHeight: '54px',
        backgroundColor: highlighted ? palette.primary.gray : palette.primary.grayTint,
        borderRadius: '8px'
    };
});

const StyledLabeled = styled(Labeled)({
    '.RaLabeled-label': {
        fontSize: '14px',
        fontWeight: 600,
        color: palette.primary.midnightBlue
    }
});

const StyledLessonCount = styled(Typography)({
    fontSize: '11px',
    color: Theme.palette.primary.grayBluish
});

const StyledHelperText = styled(Typography)({
    fontSize: '11px',
    textAlign: 'center',
    color: Theme.palette.primary.grayBluish
});

const DEFAULT_WORKING_HOURS = 40;

const ChildLabel = () => {
    const { name, surname, lessonCount } = useRecordContext();

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>{`${name} ${surname}`}</Typography>
            <StyledLessonCount>{lessonCount}</StyledLessonCount>
        </Stack>
    );
};

const EmployeeLabel = () => {
    const { name, surname, nonScheduledServicesCount } = useRecordContext();

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontSize="11px" fontWeight="600">{`${name} ${surname}`}</Typography>
            <StyledLessonCount>{nonScheduledServicesCount} год</StyledLessonCount>
        </Stack>
    );
};

const fetchSubjects = async (
    targetGradeRange: (typeof EducationLevelGradeRange)[EducationLevelGroup],
    userRole: UserRole
): Promise<ScheduleSubject[]> => {
    const params = new URLSearchParams({
        targetGradeRange
    });

    const subjectTypes = roleSubjectTypesMap[userRole];
    if (subjectTypes && subjectTypes.length > 0) {
        params.append('subjectTypes', subjectTypes.join(','));
    }

    if (subjectTypes?.includes(SUBJECT_TYPE.SPECIAL)) {
        params.append('specialRole', userRole);
    }

    const { data } = await axiosApiInstance.get<Record<EducationLevel, ScheduleSubject[]>>(
        `subjects?${params.toString()}`
    );
    const uniqueSubjects: Record<number, ScheduleSubject> = Object.values(data)
        .flat()
        .reduce((subjects: Record<number, ScheduleSubject>, subject: ScheduleSubject) => {
            subjects[subject.id] = subject;
            return subjects;
        }, {});

    return Object.values(uniqueSubjects);
};

const fetchChildren = async (
    educationLevelRange: string,
    subjectId: ScheduleSubject['id'],
    rangeStart: Date,
    educationCenterId?: number,
    hospitalId?: number,
    isSpecialTeacher?: boolean
): Promise<ScheduleChild[]> => {
    const params = new URLSearchParams({
        targetGradeRange: educationLevelRange,
        subjectId: subjectId.toString(),
        weekDate: DateTimeUtils.getFormattedDate(rangeStart),
        educationCenterId: educationCenterId ? educationCenterId.toString() : '',
        hospitalId: hospitalId ? hospitalId.toString() : '',
        ...(isSpecialTeacher ? { isSpecialTeacher: 'true' } : {})
    });

    const { data } = await axiosApiInstance.get(`children?${params.toString()}`);

    return data;
};

const educationLevelToLabelMap = [
    ...PRE_SCHOOL_YEARS_LABELS,
    ...JUNIOR_SCHOOL_GRADES_LABELS,
    ...HIGH_SCHOOL_GRADES_LABELS
].reduce<Record<string, string>>((acc, { sourceName, label }) => {
    acc[sourceName] = label;
    return acc;
}, {});

// todo: move out some common stuff
export const ScheduleSidebar: FC = () => {
    const form = useForm();
    const { calendarConfig, calendarData, setCalendarData } = useScheduleCalendarContext();
    const [calendarEvents, setCalendarEvents] = useState<ScheduleCalendarEvent[]>([]); // todo: should be only one event at a time?

    const {
        fields: childrenFields,
        update: updateChildrenField,
        remove: removeChildrenField
    } = useFieldArray<any, string, any>({
        name: 'children'
    });

    const role: UserRole = useWatch({ name: 'role', control: form.control });
    const subjectId: number = useWatch({ name: 'subject', control: form.control, defaultValue: null });
    const employeeId: number = useWatch({ name: 'employee', control: form.control, defaultValue: null });
    const childrenIds: number[][] = useWatch({
        name: 'children',
        control: form.control,
        defaultValue: []
    });

    const onlineLesson: boolean = useWatch({ name: 'onlineLesson', control: form.control, defaultValue: false });
    const classroomId: string = useWatch({ name: 'classroomId', control: form.control, defaultValue: null });
    const meetingLink: string = useWatch({ name: 'meetingLink', control: form.control, defaultValue: null });

    const isSpecialTeacher = role === UserRole.SPECIAL_EDUCATION_TEACHER;

    const { isFetching: isFetchingSubjects, data: subjects } = useQuery(
        [calendarConfig.educationLevelGroup, role],
        () => fetchSubjects(EducationLevelGradeRange[calendarConfig.educationLevelGroup], role),
        { keepPreviousData: true }
    );
    const { isFetching: isFetchingEmployees, data: employees } = useQuery(
        [
            subjectId,
            role,
            calendarConfig.educationLevelGroup,
            calendarConfig.range.start,
            calendarData.newLesson,
            calendarData.deletedEvent
        ],
        () =>
            fetchEmployeesData(
                EducationLevelGradeRange[calendarConfig.educationLevelGroup],
                role,
                subjectId,
                calendarConfig.range.start,
                calendarData.educationCenter?.id,
                true
            ),
        {
            enabled: !!role && !!subjectId,
            keepPreviousData: true
        }
    );

    // todo: newer version of react-query has placeholder data, consider update
    const { isFetching: isFetchingChildren, data: children } = useQuery<ScheduleChild[]>(
        [
            subjectId,
            calendarConfig.educationLevelGroup,
            calendarConfig.range.start,
            calendarData.newLesson,
            calendarData.deletedEvent
        ],
        () =>
            fetchChildren(
                EducationLevelGradeRange[calendarConfig.educationLevelGroup],
                subjectId,
                calendarConfig.range.start,
                calendarData.educationCenter?.id,
                calendarData.hospital?.id,
                isSpecialTeacher
            ),
        {
            enabled: !!subjectId && !!employeeId && !!role,
            keepPreviousData: true
        }
    );

    const { data: centerData } = useQuery(
        [calendarData.educationCenter?.id],
        () => fetchCenterData(calendarData.educationCenter!.id),
        {
            enabled: !!subjectId && !!employeeId
        }
    );

    const availableClassrooms = useMemo(
        () =>
            centerData?.classroomLocations
                ?.filter(
                    (classroom: any) =>
                        classroom.targetGradeRange.includes(
                            EducationLevelGradeRange[calendarConfig.educationLevelGroup]
                        ) && classroom.hospitalId === calendarData.hospital?.id
                )
                .map((classroom: any) => ({
                    id: classroom.id,
                    name: classroom.name
                })) || [],
        [calendarData, calendarConfig]
    );

    useEffect(() => {
        removeChildrenField();

        const groupedChildren = children?.length ? groupBy(children, (child) => child.educationLevel) : [];

        const sortedEntries = Object.entries(groupedChildren).sort(([a], [b]) => {
            const numA = Number(a.match(/\d+$/)?.[0] || 0);
            const numB = Number(b.match(/\d+$/)?.[0] || 0);
            return numA - numB;
        });

        sortedEntries.forEach(([grade, children], index) => {
            const choices = children.map(({ id, name, surname, lessonCount }) => ({
                id,
                name,
                surname,
                lessonCount,
                disabled: lessonCount === 0
            }));

            updateChildrenField(index, { grade, choices });
        });
    }, [children, subjectId, employeeId]);

    const resetSidebar = (options: { role?: boolean; subject?: boolean; employee?: boolean; children?: boolean }) => {
        const { role, subject, employee, children } = options;

        setCalendarEvents([]);

        if (role) {
            form.resetField('role', { defaultValue: null });
        }
        if (subject) {
            form.resetField('subject', { defaultValue: null });
        }
        if (employee) {
            form.resetField('employee', { defaultValue: null });
        }
        if (children) {
            form.resetField('children', { defaultValue: [] });
        }
    };

    useEffect(() => {
        resetSidebar({
            role: true,
            subject: true,
            employee: true,
            children: true
        });
    }, [calendarConfig.educationLevelGroup, calendarData.educationCenter]);

    useEffect(() => {
        resetSidebar({
            subject: true,
            employee: true,
            children: true
        });

        setCalendarData((calendarData) => ({ ...calendarData, role }));
    }, [role]);

    useEffect(() => {
        resetSidebar({
            employee: true,
            children: true
        });
        if (Array.isArray(subjects) && subjectId) {
            const subject = subjects.find((subject) => subject.id === subjectId);
            setCalendarData((calendarData) => ({ ...calendarData, subject }));
        }
    }, [subjectId]);

    useEffect(() => {
        resetSidebar({
            children: true
        });

        let employee: ScheduleEmployee | undefined = undefined;

        if (Array.isArray(employees) && employeeId) {
            employee = employees.find((employee) => employee.id === employeeId);
        }
        setCalendarData((calendarData) => ({
            ...calendarData,
            employee
        }));
    }, [employeeId, employees]);

    useEffect(() => {
        // todo: fix workaround // radio btn group does not emit value on choices arr change
        if (!employees || employees.length === 0) {
            form.setValue('employee', null);
            return;
        }

        if (!form.getValues('employee')) {
            form.setValue('employee', employees![0].id);
        }
    }, [employees]);
    useEffect(() => {
        setCalendarData((calendarData) => ({
            ...calendarData,
            children: [],
            employees: []
        }));
        resetSidebar({});

        if (
            Array.isArray(employees) &&
            Array.isArray(children) &&
            Array.isArray(subjects) &&
            employeeId &&
            subjectId &&
            childrenIds.flat().length
        ) {
            const subject = subjects.find((subject) => subject.id === subjectId);
            const employee = employees.find((employee) => +employee.id === +employeeId);

            const selectedChildren: ScheduleChild[] = childrenIds
                .flat()
                .filter(Boolean)
                .map((childId) => children.find((child) => child.id === childId)) as ScheduleChild[];

            setCalendarData((calendarData) => ({
                ...calendarData,
                children: selectedChildren
            }));

            setCalendarEvents((events) => {
                const id = [subject!.id, employeeId, ...selectedChildren.map((child) => child.id)].join('_');
                // todo: replace with one event only if multiple events not possible
                const event: ScheduleCalendarEvent = events.length > 0 ? { ...events[0] } : { id, title: '' };

                event.title = subject!.name;
                event.employee = employee;
                event.children = selectedChildren;

                if (onlineLesson) {
                    event.meetingLink = meetingLink;
                } else {
                    event.classroomId = classroomId !== 'other' ? classroomId : null;
                }

                if (subject?.durationMinutes) {
                    event.durationMinutes = subject?.durationMinutes;
                }
                if (subject?.consultationType) {
                    event.consultationType = subject?.consultationType;
                }
                if (isSpecialTeacher) {
                    event.isSpecialTeacher = isSpecialTeacher;
                }

                return [event];
            });
        }
    }, [employees, children, employeeId, childrenIds, subjectId, classroomId, meetingLink]);

    const isFetching = isFetchingSubjects || isFetchingEmployees || isFetchingChildren;

    return (
        <Stack sx={{ position: 'sticky', top: 80 + 16, maxHeight: 'calc(100vh - 120px)', overflowX: 'auto' }}>
            <div style={{ minHeight: '10px' }}>
                {isFetching && (
                    <LinearProgress variant="query" sx={{ '.MuiLinearProgress-bar': { transition: 'none' } }} />
                )}
            </div>
            <FormProvider {...form}>
                <ShsSelectInput
                    source="role"
                    label="Для кого складаєте розклад"
                    placeholder="Виберіть роль"
                    choices={EducationLevelToRolesMap[calendarConfig.educationLevelGroup]
                        .filter((userRole) => UserRoleToAPIPathMap[userRole]) // todo: remove once all endpoints are implemented
                        .map((userRole) => ({
                            id: userRole,
                            name: UserRoleLabel[userRole]
                        }))}
                    filter=""
                    autoFocus
                    required
                    disabled={!calendarData?.educationCenter?.id || !calendarData?.hospital?.id}
                    label-sx={{ fontSize: '11px', fontWeight: 400, color: palette.primary.midnightBlue }}
                />

                {role && (
                    <ShsSelectInput
                        source="subject"
                        label="Предмет"
                        choices={
                            subjects?.map((subject) => ({
                                id: subject.id,
                                name: subject?.consultationType
                                    ? `${subject.name} (${ConsultationType[subject?.consultationType as keyof typeof ConsultationType]})`
                                    : subject.name
                            })) || []
                        }
                        required
                        isTruncatedOptions
                        label-sx={{ fontSize: '11px', fontWeight: 400, color: palette.primary.midnightBlue }}
                    />
                )}
                {subjectId && (
                    <StyledLabeled label={UserRoleLabel[role]}>
                        {employees?.length ? (
                            <RadioButtonGroupInput
                                source="employee"
                                parse={Number}
                                choices={employees
                                    ?.sort((a, b) => a.name.localeCompare(b.name, 'uk', { sensitivity: 'base' }))
                                    ?.map(({ id, name, surname, scheduledServicesCount, maxHours }) => ({
                                        id,
                                        name,
                                        surname,
                                        nonScheduledServicesCount: (scheduledServicesCount || 0) + '/' + (maxHours || 0)
                                    }))}
                                optionText={<EmployeeLabel />}
                                label=""
                                autoFocus={false}
                                sx={{
                                    ...shsFormErrorStyles,
                                    m: 0,
                                    flexDirection: 'column',
                                    '.MuiFormControlLabel-label': {
                                        width: '100%'
                                    }
                                }}
                            />
                        ) : (
                            <StyledHelperText>Працівника не знайдено</StyledHelperText>
                        )}
                    </StyledLabeled>
                )}
                {employeeId && childrenFields.length && (
                    <>
                        <Typography fontWeight="600" fontSize="14px">
                            {calendarConfig.educationLevelGroup === EducationLevelGroup.PRESCHOOL
                                ? 'Вікова група'
                                : 'Клас'}
                        </Typography>
                        <Box>
                            {childrenFields.map((field, index) => (
                                <ShsCheckboxTree
                                    key={field.id}
                                    source={`children.${index}`}
                                    defaultValue={childrenIds[index]}
                                    label={educationLevelToLabelMap[field.grade]}
                                    isCountNeeded={UserRoleToAPIPathMap[role] === 'teachers'}
                                    {...field}
                                />
                            ))}
                        </Box>
                        <Box
                            sx={{
                                marginBottom: '16px',
                                marginTop: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flex: '1'
                            }}>
                            <Typography fontWeight="600" fontSize="14px">
                                Тип заняття
                            </Typography>
                            <ShsToggle
                                label={onlineLesson ? 'Онлайн' : 'Офлайн'}
                                source="onlineLesson"
                                sx={{
                                    '.MuiFormHelperText-root': {
                                        display: 'none'
                                    },
                                    '& .MuiSwitch-root': {
                                        height: '1.3rem',
                                        width: '40px',
                                        '& .MuiSwitch-switchBase': {
                                            padding: '4px 4px',
                                            '& .toggle-button': {
                                                width: '13px',
                                                height: '13px'
                                            }
                                        }
                                    },
                                    '.MuiTypography-root': {
                                        fontSize: '11px'
                                    }
                                }}
                            />
                        </Box>
                        {onlineLesson ? (
                            <ShsTextInput
                                source="meetingLink"
                                label="Посилання на онлайн-урок"
                                validate={regex(URL_REGEX, INVALID_URL_MESSAGE)}
                                required={onlineLesson}
                                label-sx={{ fontSize: '14px', fontWeight: 600, color: palette.primary.midnightBlue }}
                            />
                        ) : (
                            <ShsSelectInput
                                source="classroomId"
                                label="Приміщення уроку"
                                choices={[
                                    ...availableClassrooms,
                                    { id: 'other', name: 'Інше' } // Selecting this option will result in classroomId: null being sent to the backend
                                ]}
                                defaultValue={null}
                                required={!onlineLesson}
                                label-sx={{ fontSize: '14px', fontWeight: 600, color: palette.primary.midnightBlue }}
                            />
                        )}
                    </>
                )}
            </FormProvider>

            {subjectId && employeeId && !!childrenIds.flat().length && !!calendarEvents.length && (
                <StyledBox sx={{ marginBottom: '16px' }}>
                    {calendarEvents.map((event) => (
                        <ScheduleEventCard
                            key={event.id}
                            event={event}
                            dragIcon
                            enabled={
                                !!event.children?.length &&
                                !!event.employee &&
                                !event.children.find((x) => x.lessonCount === 0) &&
                                event.employee.staffRate * DEFAULT_WORKING_HOURS -
                                    event.employee.scheduledServicesCount !==
                                    0 &&
                                ((!!meetingLink && onlineLesson) || (!!classroomId && !onlineLesson))
                            }
                        />
                    ))}
                </StyledBox>
            )}

            {!!calendarEvents.length && (
                <StyledHelperText>Щоб додати урок, перетягніть картку на потрібну годину</StyledHelperText>
            )}

            {employeeId && subjectId && !calendarEvents.length && (
                <StyledHelperText>Всі уроки з цього предмету додані в розклад</StyledHelperText>
            )}
        </Stack>
    );
};
